import { useState } from 'react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import FormRow from '../../components/FormRow'
import TextField from '../../components/TextField'
import { FormSection, SwitchField, SwitchGroup } from '../../components/Form'
import { useToast } from '../../components/toastContext'
import { setExternalPolicyUrl, setPoliciesPageTitle, setPoliciesViewing } from './engagementService'
import { policiesStrings as S } from './strings'
import { serverDetail, serverText } from '../../lib/i18n'

/** The legacy's own bounds for each title (`updatePageTitle` in the settings js). */
const titleValid = (value) => value.length >= 2 && value.length <= 200

/** An address the browser can actually open — http or https, with a host. */
const urlValid = (value) => {
  try {
    const url = new URL(value)
    return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.hostname)
  } catch {
    return false
  }
}

/**
 * إعدادات السياسات — the legacy `settings_tab`, element for element:
 *
 *   تفعيل خاصية السياسات                   a switch, written the moment it moves
 *   عرض السياسات في شكل مصفوفة بدون تصنيف   a switch, written the moment it moves
 *   عنوان صفحة السياسات                     two required titles and their own تعديل
 *   رابط نظام السياسات الخارجي              one address and its own حفظ
 *
 * Each is its own endpoint, so each saves on its own, as the legacy does. The legacy's
 * category table lives on the التصنيفات tab beside this one.
 *
 * @param {boolean} enabled                 whether the feature is on at all
 * @param {(on: boolean) => void} onToggle  saves at once — see useFeatureToggle
 */
export default function PolicySettingsPanel({ settings, onChange, enabled, onToggle }) {
  const toast = useToast()
  const [titleAr, setTitleAr] = useState(settings.pageTitleAr)
  const [titleEn, setTitleEn] = useState(settings.pageTitleEn)
  const [externalUrl, setExternalUrl] = useState(settings.externalUrl)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState('')

  const report = (outcome, fallback) => {
    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || S.fail.settings)
      return false
    }
    toast.success(serverText(outcome.message) || fallback)
    return true
  }

  /* Moves on press and goes back if the server refuses — the same contract as the
     feature switch above it. */
  const toggleViewing = async (showAsGrid) => {
    if (saving === 'viewing') return
    setSaving('viewing')
    onChange({ showAsGrid })
    const outcome = await setPoliciesViewing(showAsGrid)
    setSaving('')
    if (!report(outcome, S.saved)) onChange({ showAsGrid: !showAsGrid })
  }

  const saveTitle = async () => {
    const ar = titleAr.trim()
    const en = titleEn.trim()
    const found = {
      titleAr: titleValid(ar) ? undefined : S.settings.pageTitleInvalid,
      titleEn: titleValid(en) ? undefined : S.settings.pageTitleInvalid,
    }
    setErrors((previous) => ({ ...previous, ...found }))
    if (found.titleAr || found.titleEn) return

    setSaving('title')
    const outcome = await setPoliciesPageTitle(ar, en)
    setSaving('')
    if (report(outcome, S.saved)) onChange({ pageTitleAr: ar, pageTitleEn: en })
  }

  const saveUrl = async () => {
    const url = externalUrl.trim()
    /* Empty is allowed: it is how the built-in page is put back. */
    if (url && !urlValid(url)) {
      setErrors((previous) => ({ ...previous, externalUrl: S.settings.urlInvalid }))
      return
    }

    setSaving('url')
    const outcome = await setExternalPolicyUrl(url)
    setSaving('')
    if (report(outcome, S.saved)) onChange({ externalUrl: url })
  }

  const edit = (setter, key) => (value) => {
    setter(value)
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  return (
    <Card title={S.sections.settings}>
      <div className="polset">
        <SwitchGroup>
          <SwitchField checked={enabled} onChange={onToggle} label={S.enableFeature} />
          <SwitchField checked={settings.showAsGrid} onChange={toggleViewing} label={S.settings.viewAsGrid} />
        </SwitchGroup>

        <FormSection title={S.settings.pageTitle}>
          <div className="polset__col">
            <FormRow label={S.settings.pageTitleAr} required error={errors.titleAr}>
              {(id) => (
                <TextField
                  id={id}
                  value={titleAr}
                  onChange={edit(setTitleAr, 'titleAr')}
                  invalid={!!errors.titleAr}
                  maxLength={200}
                />
              )}
            </FormRow>
            <FormRow label={S.settings.pageTitleEn} required error={errors.titleEn}>
              {(id) => (
                <TextField
                  id={id}
                  dir="ltr"
                  value={titleEn}
                  onChange={edit(setTitleEn, 'titleEn')}
                  invalid={!!errors.titleEn}
                  maxLength={200}
                />
              )}
            </FormRow>
            <div>
              <Button onClick={saveTitle} busy={saving === 'title'} disabled={saving === 'title'}>
                {S.settings.update}
              </Button>
            </div>
          </div>
        </FormSection>

        <FormRow label={S.settings.externalUrl} hint={S.settings.externalUrlHint} error={errors.externalUrl}>
          {(id) => (
            <div className="polset__url">
              <TextField
                id={id}
                dir="ltr"
                type="url"
                value={externalUrl}
                onChange={edit(setExternalUrl, 'externalUrl')}
                invalid={!!errors.externalUrl}
                placeholder="https://"
              />
              <Button variant="secondary" onClick={saveUrl} busy={saving === 'url'} disabled={saving === 'url'}>
                {S.settings.save}
              </Button>
            </div>
          )}
        </FormRow>
      </div>
    </Card>
  )
}
